import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { tenantService } from '../services/tenantService';
import { GOLFOS_TENANT_CODE, isGolfOSVariant } from '../config/branding';

export interface Tenant {
  code: string;
  name: string | null;
  description: string | null;
  logoFileId: number | null;
  logoThumbnail: string | null;
  logoUrl: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  isDefault?: boolean;
  // Franchise fields
  enterpriseId?: string | null;
  brandId?: string | null;
  regionId?: string | null;
  franchiseeOwner?: string | null;
  franchiseeEmail?: string | null;
  franchiseePhone?: string | null;
  complianceScore?: number | null;
  royaltyRate?: number | null;
}

interface TenantContextType {
  availableTenants: Tenant[];
  currentTenant: Tenant | null;
  loading: boolean;
  error: string | null;
  switchTenant: (tenantCode: string) => void;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const fetchAvailableTenants = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch tenants directly from API without going through dataProvider
      // This avoids the circular dependency where dataProvider needs tenant but we need to fetch tenants
      const accessToken = localStorage.getItem('accessToken');
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

      const response = await fetch(`${baseUrl}/tenants`, {
        method: 'GET',
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Clear authentication data and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return; // Stop execution here
        }
        throw new Error(`Failed to fetch tenants: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      const data = result.data || result; // Handle both paginated and direct array responses

      console.log('TenantContext: Fetched tenants response:', { result, data });

      const tenants = data.map((tenant: any) => {
        return {
          code: tenant.code,
          name: tenant.name,
          description: tenant.description,
          logoFileId: tenant.logoFileId,
          logoThumbnail: tenant.logoThumbnail,
          logoUrl: tenant.logoUrl,
          createdAt: tenant.createdAt,
          updatedAt: tenant.updatedAt,
          isDefault: tenant.isDefault,
          // Franchise fields
          enterpriseId: tenant.enterpriseId,
          brandId: tenant.brandId,
          regionId: tenant.regionId,
          franchiseeOwner: tenant.franchiseeOwner,
          franchiseeEmail: tenant.franchiseeEmail,
          franchiseePhone: tenant.franchiseePhone,
          complianceScore: tenant.complianceScore,
          royaltyRate: tenant.royaltyRate,
        };
      });

      // Filter out default tenants - users should never access these
      const filteredTenants = tenants.filter((tenant: any) => {
        const tenantCode = tenant.code.toLowerCase();
        const tenantName = (tenant.name || '').toLowerCase();
        
        return !tenantCode.includes('default') && 
               !tenantCode.includes('sample') &&
               !tenantName.includes('default') &&
               !tenantName.includes('sample') &&
               tenant.code !== 'default';
      });

      console.log('TenantContext: Setting available tenants:', filteredTenants);

      // GolfOS: single club workspace — only the configured tenant code
      let tenantsForApp = filteredTenants;
      if (isGolfOSVariant) {
        const dg = filteredTenants.filter(
          (t: Tenant) => t.code.toLowerCase() === GOLFOS_TENANT_CODE.toLowerCase(),
        );
        if (dg.length === 0) {
          setError(
            `GolfOS requires access to the "${GOLFOS_TENANT_CODE.toUpperCase()}" club workspace. Your account is not assigned to this tenant.`,
          );
          setAvailableTenants([]);
          setCurrentTenant(null);
          tenantService.clearCurrentTenant();
          setLoading(false);
          return;
        }
        tenantsForApp = dg;
      }

      setAvailableTenants(tenantsForApp);

      // Enterprise admin users have no tenants - that's OK
      // They operate at the enterprise level, not tenant level
      if (tenantsForApp.length === 0) {
        console.log('TenantContext: No tenants available - enterprise admin mode');
        setCurrentTenant(null);
        tenantService.clearCurrentTenant();
        setLoading(false);
        return;
      }

      // Check if there's a stored current tenant
      const storedTenant = tenantService.getCurrentTenant();
      let currentTenant = null;

      if (storedTenant) {
        // Only use stored tenant if it's in our filtered list (not a default tenant)
        currentTenant = tenantsForApp.find((t: Tenant) => t.code === storedTenant.code);
      }

      // If no stored tenant or stored tenant not found, use first available tenant (never default)
      if (!currentTenant && tenantsForApp.length > 0) {
        currentTenant = tenantsForApp[0];
      }

      // GolfOS: always lock to the fixed tenant
      if (isGolfOSVariant && currentTenant) {
        currentTenant =
          tenantsForApp.find(t => t.code.toLowerCase() === GOLFOS_TENANT_CODE.toLowerCase()) ||
          currentTenant;
      }

      if (currentTenant) {
        setCurrentTenant(currentTenant);
        tenantService.setCurrentTenant(currentTenant);
      }
    } catch (error) {
      console.error('Failed to fetch tenants from API:', error);
      
      // Check if this is a 401 error (could happen in fetch rejection)
      if (error instanceof Error && error.message.includes('401')) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return;
      }
      
      setError(
        `Failed to load tenants: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      setAvailableTenants([]);
      setCurrentTenant(null);
      tenantService.clearCurrentTenant();
    }

    setLoading(false);
  };

  const switchTenant = (tenantCode: string) => {
    if (isGolfOSVariant) {
      if (notify) {
        notify('Club workspace is fixed for GolfOS.', { type: 'info' });
      }
      return;
    }

    // Prevent switching to default/sample tenants
    const tenantCodeLower = tenantCode.toLowerCase();
    if (tenantCodeLower.includes('default') || tenantCodeLower.includes('sample') || tenantCode === 'default') {
      if (notify) {
        notify('Access to this tenant is not allowed', { type: 'error' });
      }
      return;
    }

    const tenant = availableTenants.find(t => t.code === tenantCode);
    if (tenant) {
      const previousTenant = currentTenant;
      setCurrentTenant(tenant);
      tenantService.setCurrentTenant(tenant);

      // Only refresh if we're actually switching to a different tenant
      if (previousTenant && previousTenant.code !== tenant.code) {
        // Refresh the current page to reload data with new tenant context
        refresh();
      }

      if (notify) {
        notify(`Switched to: ${tenant.name || tenant.code}`, { type: 'success' });
      }
    } else {
      if (notify) {
        notify('Invalid tenant selection', { type: 'error' });
      }
    }
  };

  const refreshTenants = async () => {
    await fetchAvailableTenants();
  };

  useEffect(() => {
    if (dataProvider) {
      fetchAvailableTenants();
    }
  }, [dataProvider]);

  return (
    <TenantContext.Provider
      value={{
        availableTenants,
        currentTenant,
        loading,
        error,
        switchTenant,
        refreshTenants,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
